<template>
  <b-container
    fluid
    class="welcome-user"
    :class="hasError ? 'error-banner' : 'ok-banner'">
    <div class="row">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
    <div class="row" v-if="!mobileView">
      <div
        class="col-sm-12 text-left header-msg"
        v-if="IsAuthenticated && !hasError">
        <div class="row">
          <div class="col-lg-10 text-left">
            <h4>
              <span
                v-if="UserData.details.role != 'not_specified'"
                class="user-line">
                <strong class="user-fullname">{{
                  UserData.details.firstName + ' ' + UserData.details.lastName
                }}</strong>
                <span
                  class="notif-bell"
                  :class="{ 'is-quiet': !GetTotalNewNotifications() }"
                  id="notif-btn"
                  v-b-tooltip.hover.right="{ customClass: 'notif-tip' }"
                  :title="NotifTooltip">
                  <b-icon
                    :icon="GetTotalNewNotifications() ? 'bell-fill' : 'bell'"
                    aria-hidden="true"></b-icon>
                  <span
                    v-if="GetTotalNewNotifications()"
                    class="notif-bell-count"
                    >{{ NotifCountLabel }}</span
                  >
                </span>
              </span>
              <strong v-else>Welcome!</strong>
            </h4>
          </div>
          <div class="col-lg-2 text-right">
            <b-badge
              v-if="UserData.details.role != 'not_specified'"
              variant="primary"
              style="text-transform: uppercase"
              >{{ UserData.details.role }}</b-badge
            >
          </div>
        </div>
      </div>
      <div class="col-sm-12 header-msg text-left" v-else>
        <h4>
          <strong>Your current session has expired. Please wait...</strong>
        </h4>
      </div>
    </div>
    <div class="row" v-if="mobileView">
      <div
        class="col-sm-12 text-left header-msg"
        v-if="IsAuthenticated && !hasError">
        <div class="row">
          <div class="col-lg-12 text-left">
            <span
              v-if="UserData.details.role != 'not_specified'"
              class="user-line">
              <strong class="user-fullname">{{
                UserData.details.firstName + ' ' + UserData.details.lastName
              }}</strong>
              <span
                class="notif-bell"
                :class="{ 'is-quiet': !GetTotalNewNotifications() }"
                id="notif-btn"
                v-b-tooltip.hover.right="{ customClass: 'notif-tip' }"
                :title="NotifTooltip">
                <b-icon
                  :icon="GetTotalNewNotifications() ? 'bell-fill' : 'bell'"
                  aria-hidden="true"></b-icon>
                <span v-if="GetTotalNewNotifications()" class="notif-bell-count"
                  >{{ NotifCountLabel }}</span
                >
              </span>
            </span>
            <strong v-else>Welcome!</strong>
          </div>
        </div>
      </div>
      <div class="col-sm-12 header-msg text-left" v-else>
        <strong
          >Your current session has expired. Please refresh the page and sign in
          again.</strong
        >
      </div>
    </div>
    <div class="row">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>

    <b-popover
      v-if="IsAuthenticated && !hasError && GetTotalNewNotifications()"
      target="notif-btn"
      triggers="hover"
      id="notif-popover"
      placement="bottomright">
      <template #title>
        <span class="notif-title">Notifications</span>
        <span class="notif-count">{{ GetTotalNewNotifications() }}</span>
      </template>

      <div class="notif-list">
        <div
          class="notif-item"
          v-for="notif in GetNotications()"
          :key="notif.uid"
          v-b-modal.notif-modal
          @click="GetNoticationsInfo(notif.uid)">
          <span
            class="notif-avatar"
            v-b-tooltip.hover.left="{ customClass: 'notif-tip' }"
            :title="GetNotificationOwner(notif.owner, false)"
            >{{ GetNotificationOwner(notif.owner) }}</span
          >
          <span class="notif-body">
            <span class="notif-subject">{{ notif.subject }}</span>
            <span class="notif-time">
              <b-icon icon="clock" aria-hidden="true"></b-icon>
              {{ notif.date }}
            </span>
          </span>
          <b-icon
            icon="chevron-right"
            class="notif-chevron"
            aria-hidden="true"></b-icon>
        </div>
      </div>

      <div class="notif-foot">
        <b-button
          size="sm"
          variant="link"
          class="btn-mark-all-read"
          @click="markAllNotifAsRead"
          >Mark all as read</b-button
        >
      </div>
    </b-popover>

    <b-modal
      id="notif-modal"
      size="lg"
      centered
      scrollable
      modal-class="lz-activity-modal notif-modal"
      content-class="lz-modal-content"
      body-class="lz-modal-body"
      header-bg-variant="white"
      header-text-variant="dark"
      body-bg-variant="white"
      body-text-variant="dark"
      footer-bg-variant="white"
      title="Notification"
      @hidden="notifications.modal.data = {}">
      <template #modal-header="{ close }">
        <div class="notif-modal-head">
          <span class="notif-modal-avatar">{{ NotifModalInitials }}</span>
          <div class="notif-modal-heading">
            <h5 class="notif-modal-subject">
              {{ notifications.modal.data.subject || 'Notification' }}
            </h5>
            <div class="notif-modal-meta">
              <span v-if="notifications.modal.data.owner">{{
                notifications.modal.data.owner
              }}</span>
              <span
                v-if="notifications.modal.data.owner"
                class="notif-modal-sep"
                >&middot;</span
              >
              <span>
                <b-icon icon="clock" aria-hidden="true"></b-icon>
                {{ notifications.modal.data.date }}
              </span>
            </div>
          </div>
        </div>
        <b-button
          variant="link"
          class="notif-modal-close"
          aria-label="Close"
          @click="close()">
          <b-icon icon="x" aria-hidden="true"></b-icon>
        </b-button>
      </template>

      <div v-if="!notifications.modal.data.uid" class="notif-modal-loading">
        <b-skeleton width="90%"></b-skeleton>
        <b-skeleton width="75%"></b-skeleton>
        <b-skeleton width="85%"></b-skeleton>
      </div>
      <div v-else class="notif-modal-content" v-html="notifications.modal.data.body"></div>

      <template #modal-footer="{ close }">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="lz-close-btn"
          @click="close()"
          >Close</b-button
        >
        <b-button
          size="sm"
          variant="primary"
          class="lz-close-btn notif-modal-read-btn"
          :disabled="!notifications.modal.data.uid"
          @click="markNotifAsRead(notifications.modal.data.uid, close)">
          Mark as read
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import Services from '../services/main.service.vue'

export default {
  name: 'WelcomeUser',
  props: {
    mobileView: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      hasError: false,
      notifications: {
        modal: {
          data: {}
        }
      }
    }
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    UserData() {
      return this.Store.userAuth
    },
    IsAuthenticated() {
      return this.UserData.authenticated
    },
    UserNotifications() {
      return this.Store.userNotifications
    },
    NotifCountLabel() {
      const total = this.GetTotalNewNotifications()
      return total > 99 ? '99+' : String(total)
    },
    NotifTooltip() {
      const total = this.GetTotalNewNotifications()
      if (!total) {
        return 'No new notifications'
      }
      return `${total} new notification${total > 1 ? 's' : ''}`
    },
    NotifModalInitials() {
      // `owner` is flattened to "First Last" by GetNoticationsInfo()
      const owner = this.notifications.modal.data.owner
      if (!owner) return ''
      return owner
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')
    }
  },
  methods: {
    GetTotalNewNotifications() {
      // console.log(this.UserNotifications)
      // return 0
      return this.UserNotifications?.total ?? 0
    },
    GetNotications() {
      if (this.GetTotalNewNotifications()) {
        return this.UserNotifications?.list ?? []
      }
      return []
    },
    GetNotificationOwner(owner, initials = true) {
      if (initials) {
        return (
          owner.firstName.charAt(0).toUpperCase() +
          owner.lastName.charAt(0).toUpperCase()
        )
      }
      return owner.firstName + ' ' + owner.lastName
    },
    async GetNoticationsInfo(notifUID) {
      this.notifications.modal.data = {}
      const { status, data } = await Services.Users.getNotificationInfo(
        notifUID
      )
      if (status === 200) {
        data.content.owner = this.GetNotificationOwner(
          data.content.owner,
          false
        )
        this.notifications.modal.data = data.content
      }
    },
    markAllNotifAsRead() {
      this.markNotifAsRead(null, () =>
        this.$root.$emit('bv::hide::popover', 'notif-btn')
      )
    },
    async markNotifAsRead(notifUID = null, cb = () => {}) {
      // if notifUID was null all notifications will be marked as read
      const { status } = await Services.Users.markNotificationAsRead(notifUID)
      if (status === 200) {
        this.$bvToast.toast('Notification is marked as read.', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
        Services.Users.fetchCurrentUser()
          .then(({ status, data }) => {
            // if (status !== 200) {
            //   this.$root.$emit('user-signed-out')
            // }
            this.$store.commit(
              'updateNotifications',
              data?.content?.notifications ?? {}
            )
          })
          .catch(() => {
            // this.$root.$emit('user-signed-out')
          })
      }
      cb()
    }
  },
  watch: {
    IsAuthenticated() {
      this.hasError = !this.IsAuthenticated
    }
  }
}
</script>

<style scoped>
.welcome-user {
  border: none !important;
  transition: all 0.3s ease;
  padding: 0.85rem 1.5rem !important;
  font-family: Arial, sans-serif !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.welcome-user.error-banner {
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.85) 0%, rgba(153, 27, 27, 0.85) 100%) !important;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.1);
}

.welcome-user.ok-banner {
  background: linear-gradient(135deg, rgba(0, 132, 218, 0.85) 0%, rgba(56, 182, 255, 0.85) 100%) !important;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.05);
}

.lizard-form-empty-col {
  min-height: 0px !important;
  height: 0px !important;
  display: none !important;
}

.header-msg {
  color: #f8fafc !important;
  font-weight: 500 !important;
  width: 100%;
}

.header-msg h4 {
  font-size: 1.1rem !important;
  letter-spacing: -0.01em;
  margin: 0;
}

.header-msg strong {
  font-weight: 600 !important;
  color: #ffffff;
}

/* Flex rather than vertical-align so the bell centres on the name itself,
   independent of the two different fonts and their baselines. */
.header-msg .user-line {
  display: inline-flex;
  align-items: center;
}

/* Myriad Pro, declared as @font-face in App.vue. Its ascent/descent metrics
   are lopsided, so the default line box leaves the glyphs sitting high next
   to the bell. line-height 1 trims the box back to the text. */
.header-msg .user-fullname {
  display: inline-block;
  font-family: 'CANDESIC-FONT', Arial, Helvetica, sans-serif !important;
  font-size: 1.2rem;
  line-height: 1;
  letter-spacing: 0.01em;
}

.header-msg .badge-primary {
  background-color: rgba(255, 255, 255, 0.12) !important;
  color: #e2e8f0 !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.05em;
  padding: 0.35em 0.8em !important;
  border-radius: 6px !important;
  text-transform: uppercase;
}

/* Always on the banner. The red count bubble and the popover only appear
   when there is something unread; with an empty inbox the bell dims and
   stops behaving like a control. */
.notif-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-left: 10px;
  vertical-align: middle;
  border-radius: 50%;
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.notif-bell:hover {
  background-color: rgba(255, 255, 255, 0.26);
  transform: translateY(-1px);
}

.notif-bell.is-quiet {
  color: rgba(255, 255, 255, 0.65);
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.14);
  cursor: default;
}

.notif-bell.is-quiet:hover {
  background-color: rgba(255, 255, 255, 0.14);
  transform: none;
}

.notif-bell > svg {
  width: 12px;
  height: 12px;
}

.notif-bell-count {
  position: absolute;
  top: -4px;
  right: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
  background-color: #e11d48;
  border: 1.5px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(190, 18, 60, 0.5);
}

</style>

<style>
/* Tooltips on the bell and on the avatars inside the popover. Bootstrap's
   variants are either washed out or clash with the popover, so they share
   one slate skin matching #notif-popover. Tooltips render at body level,
   hence the unscoped block. */
.notif-tip .tooltip-inner {
  max-width: 240px;
  padding: 7px 11px;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: #f1f5f9;
  background-color: #1e293b;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(2, 6, 23, 0.3);
}

.notif-tip.bs-tooltip-top .arrow::before {
  border-top-color: #1e293b;
}
.notif-tip.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #1e293b;
}
.notif-tip.bs-tooltip-left .arrow::before {
  border-left-color: #1e293b;
}
.notif-tip.bs-tooltip-right .arrow::before {
  border-right-color: #1e293b;
}

#notif-popover {
  width: 360px;
  max-width: 360px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(148, 163, 184, 0.18) !important;
  box-shadow: 0 18px 40px rgba(2, 6, 23, 0.35) !important;
  background-color: #1e293b !important;
  color: #f1f5f9 !important;
  font-family: Arial, sans-serif;
  overflow: hidden;
}

@media (max-width: 420px) {
  #notif-popover {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px) !important;
  }
}

#notif-popover .arrow::before {
  border-bottom-color: rgba(148, 163, 184, 0.18) !important;
  border-top-color: rgba(148, 163, 184, 0.18) !important;
}

#notif-popover .arrow::after {
  border-bottom-color: #0f172a !important;
  border-top-color: #1e293b !important;
}

#notif-popover .popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background-color: #0f172a !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12) !important;
  border-radius: 0 !important;
  padding: 0.7rem 0.9rem !important;
}

#notif-popover .notif-title {
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

#notif-popover .notif-count {
  background-color: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
}

#notif-popover .popover-body {
  padding: 0 !important;
  color: inherit !important;
}

#notif-popover .notif-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 0.35rem;
}

#notif-popover .notif-list::-webkit-scrollbar {
  width: 6px;
}

#notif-popover .notif-list::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.3);
  border-radius: 999px;
}

#notif-popover .notif-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.65rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

#notif-popover .notif-item + .notif-item {
  margin-top: 2px;
}

#notif-popover .notif-item:hover {
  background-color: rgba(148, 163, 184, 0.1);
}

#notif-popover .notif-avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

#notif-popover .notif-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

#notif-popover .notif-subject {
  color: #e2e8f0;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

#notif-popover .notif-time {
  color: #7c8aa1;
  font-size: 0.7rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

#notif-popover .notif-chevron {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 0.7rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

#notif-popover .notif-item:hover .notif-chevron {
  opacity: 1;
}

#notif-popover .notif-foot {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  padding: 0.35rem 0.6rem;
  background-color: rgba(15, 23, 42, 0.5);
}

#notif-popover .btn-mark-all-read {
  font-size: 0.74rem !important;
  color: #38bdf8 !important;
  font-weight: 600 !important;
  padding: 0.25rem 0.4rem !important;
  text-decoration: none !important;
}

#notif-popover .btn-mark-all-read:hover,
#notif-popover .btn-mark-all-read:focus {
  color: #7dd3fc !important;
  text-decoration: underline !important;
  box-shadow: none !important;
}

/* ---------------------------------------------------- Notification modal */
/* Rides on the shared `.lz-activity-modal` skin from styles/logs.css; the
   rules below only cover the parts unique to this dialog. b-modal renders
   outside the component, so these cannot be scoped. */
.notif-modal .modal-header {
  align-items: flex-start;
}

.notif-modal .notif-modal-head {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.notif-modal .notif-modal-avatar {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(0, 115, 190, 0.1);
  color: #0073be;
  font-size: 0.85rem;
  font-weight: 700;
}

.notif-modal .notif-modal-heading {
  min-width: 0;
}

.notif-modal .notif-modal-subject {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: #1e293b;
  letter-spacing: -0.01em;
}

.notif-modal .notif-modal-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 3px;
  font-size: 12px;
  color: #64748b;
}

.notif-modal .notif-modal-sep {
  color: #cbd5e1;
}

.notif-modal .notif-modal-close.btn {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 1.1rem;
  line-height: 1;
  text-decoration: none;
}

.notif-modal .notif-modal-close.btn:hover,
.notif-modal .notif-modal-close.btn:focus {
  color: #475569;
  box-shadow: none;
}

.notif-modal .lz-modal-body {
  background-color: #ffffff;
}

.notif-modal .notif-modal-content {
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
}

.notif-modal .notif-modal-content p:last-child {
  margin-bottom: 0;
}

.notif-modal .notif-modal-content a {
  color: #0073be;
  font-weight: 600;
}

.notif-modal .notif-modal-loading > * {
  margin-bottom: 10px;
}

.notif-modal .notif-modal-read-btn.btn {
  background-color: #0073be;
  border-color: #0073be;
}

.notif-modal .notif-modal-read-btn.btn:hover:not(:disabled),
.notif-modal .notif-modal-read-btn.btn:focus:not(:disabled) {
  background-color: #005e99;
  border-color: #005e99;
}
</style>
